import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { Alert, Badge, Modal, Nav, Tab } from 'react-bootstrap'
import { CloseIcon, FeatherLoader } from '@abroad/components'
import { SelectionField, Toggle } from '../common'
import ScheduleDatePicker from '../scheduling/ScheduleDatePicker'
import ScheduleTimeSlots from '../scheduling/ScheduleTimeSlots'
import { getSortedTimezones } from '../../utils/timezone'
import useCoachCalendarPreview from './useCoachCalendarPreview'
import {
  formatProvider,
  formatTimeRange,
  getEventKey,
  getEventsForDay,
} from './coachCalendarPreviewUtils'

const TAB_KEYS = {
  CALENDARS: 'calendars',
  LISTING: 'listing',
}

const timezoneOptions = getSortedTimezones()

const CoachCalendarPreviewModal = ({ show, onHide, coachId, coachName }) => {
  const [activeTab, setActiveTab] = useState(TAB_KEYS.CALENDARS)
  const preview = useCoachCalendarPreview({ show, coachId })
  const {
    selectedMonth,
    setSelectedMonth,
    isMonthPending,
    selectedDate,
    setSelectedDate,
    timezone,
    setPickedTimezone,
    isBusy,
    slotDays,
    slotsLoading,
    slotsError,
    liveEvents,
    eventsLoading,
    eventsError,
    accounts,
    accountsLoading,
    accountsError,
  } = preview

  const selectedMoment = selectedDate
    ? moment.tz(selectedDate, 'YYYY-MM-DD', timezone)
    : null
  const isSelectedPast =
    !!selectedMoment &&
    selectedMoment.isBefore(moment.tz(timezone).startOf('day'))
  const daySlots = slotDays[selectedDate]?.slots || []
  const dayEvents = getEventsForDay(liveEvents, selectedDate, timezone)

  const renderSlotsPanel = () => {
    if (isSelectedPast) {
      return (
        <p className='sa5 text-muted mb-0'>
          Past dates are not shown in this preview.
        </p>
      )
    }
    if (slotsError) {
      return <Alert variant='danger'>{slotsError}</Alert>
    }
    return (
      <ScheduleTimeSlots
        slots={daySlots}
        isLoading={slotsLoading || isMonthPending}
        timezone={timezone}
      />
    )
  }

  const renderEventsPanel = () => {
    if (isSelectedPast) {
      return (
        <p className='sa5 text-muted mb-0'>
          Past dates are not shown in this preview.
        </p>
      )
    }
    if (eventsError) {
      return <Alert variant='danger'>{eventsError}</Alert>
    }
    if (eventsLoading || isMonthPending) {
      return (
        <div className='coach-calendar-preview__loader'>
          <FeatherLoader />
        </div>
      )
    }
    if (dayEvents.length === 0) {
      return (
        <p className='sa5 text-muted mb-0'>No calendar events on this day.</p>
      )
    }
    return (
      <ul className='list-unstyled mb-0'>
        {dayEvents.map((evt, index) => (
          <li key={getEventKey(evt, index)} className='border-bottom py-2'>
            <div className='s6 font-semibold text-break'>
              {evt.title || 'Untitled event'}
            </div>
            <div className='sa5 text-muted'>
              {formatTimeRange(evt, timezone)}
              {' · '}
              {evt.calendar?.name || 'Unknown'}
            </div>
          </li>
        ))}
      </ul>
    )
  }

  const renderListingTab = () => (
    <>
      <div className='border border-dark overflow-hidden coach-calendar-preview__box'>
        <div className='p-3 coach-calendar-preview__panel coach-calendar-preview__picker'>
          <ScheduleDatePicker
            currentMonth={selectedMonth}
            selectedDate={selectedDate}
            onMonthChange={(month) =>
              month && setSelectedMonth(moment(month).startOf('month'))
            }
            onDateSelect={setSelectedDate}
            availableDays={slotDays}
            disablePastDates
            allowDaysWithoutSlots
            isLoading={isBusy}
          />
        </div>
        <div className='px-4 py-3 d-flex flex-column coach-calendar-preview__panel'>
          {selectedMoment && (
            <h5 className='s3 mb-2'>
              {selectedMoment.format('dddd, D MMMM YYYY')}
            </h5>
          )}
          <div className='coach-calendar-preview__slots'>
            {renderSlotsPanel()}
          </div>
        </div>
        <div className='px-4 py-3 d-flex flex-column coach-calendar-preview__panel'>
          <h5 className='s3 mb-2'>Calendar events</h5>
          <div className='coach-calendar-preview__scroll'>
            {renderEventsPanel()}
          </div>
        </div>
      </div>
    </>
  )

  const renderCalendarsTab = () => {
    if (accountsError) {
      return <Alert variant='danger'>{accountsError}</Alert>
    }
    if (accountsLoading) {
      return (
        <div className='text-center py-3'>
          <FeatherLoader />
        </div>
      )
    }
    if (accounts.length === 0) {
      return <p className='sa5 text-muted mb-0'>No connected calendars.</p>
    }
    return accounts.map((account) => (
      <div key={account._id || account.id} className='mb-4'>
        <h5 className='s3 text-break mb-3'>
          {account.email} :{' '}
          {account.isAbroadCalendar
            ? 'Abroad'
            : formatProvider(account.provider)}{' '}
          Calendar
          {!account.isActive && (
            <Badge variant='warning' className='ml-2'>
              Needs reconnect
            </Badge>
          )}
        </h5>
        <div className='pl-3'>
          {(account.calendars || []).map((calendar) => (
            <div key={calendar.id} className='d-flex align-items-center pb-3'>
              <div>
                <Toggle
                  id={`coach-preview-toggle-${account._id || account.id}-${
                    calendar.id
                  }`}
                  checked={!!calendar.isSelected}
                  onChange={() => {}}
                  disabled
                />
              </div>
              <div className='flex-grow-1 ml-3'>
                <span className='s6'>
                  {calendar.name || 'Unknown'}
                  {calendar.isDefault && ' (Default)'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='xl'
      className='border-modal custom__modal coach-calendar-preview-modal'
      scrollable={true}
      centered>
      <Modal.Header className='position-relative'>
        <Modal.Title>
          <h3 className='s2 title mb-1'>Calendar preview</h3>
          <p className='sa5 mb-0 font-weight-normal'>
            {coachName ? `${coachName} · ` : ''}Read-only. Nothing in this
            preview can be changed.
          </p>
        </Modal.Title>
        <div
          role='button'
          aria-label='Close'
          title='Close'
          className='rounded-circle quest-close-icon position-absolute p-2'
          style={{
            zIndex: 10,
            cursor: 'pointer',
            top: 12,
            right: 12,
            transform: 'none',
          }}
          onClick={onHide}>
          <CloseIcon
            className='d-flex align-items-center justify-content-center'
            fontSize='10px'
            fontWeight='bold'
            color='rgb(10, 34, 60)'
            discardMargin={true}
          />
        </div>
      </Modal.Header>
      <Modal.Body className='p-3'>
        <Tab.Container
          activeKey={activeTab}
          onSelect={(key) => key && setActiveTab(key)}>
          <div className='coach-calendar-preview__header mb-3'>
            <div className='coach-pairing-tabs coach-calendar-preview__tabs'>
              <Nav variant='pills'>
                <Nav.Item>
                  <Nav.Link eventKey={TAB_KEYS.CALENDARS}>
                    MY CALENDARS
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey={TAB_KEYS.LISTING}>LISTING</Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            {activeTab === TAB_KEYS.LISTING && (
              <div className='coach-calendar-preview__timezone'>
                <SelectionField
                  options={timezoneOptions}
                  getValueAsOption
                  optionplaceholder='Select time zone'
                  isUseFormikSelectionField={false}
                  value={timezone}
                  onChange={(event) =>
                    setPickedTimezone(event?.target?.value || null)
                  }
                  className='custom-timezone-select'
                  disabled={isBusy}
                />
              </div>
            )}
          </div>
          <Tab.Content>
            <Tab.Pane eventKey={TAB_KEYS.CALENDARS}>
              {renderCalendarsTab()}
            </Tab.Pane>
            <Tab.Pane eventKey={TAB_KEYS.LISTING}>
              {renderListingTab()}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
    </Modal>
  )
}

CoachCalendarPreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  coachId: PropTypes.string,
  coachName: PropTypes.string,
}

export default CoachCalendarPreviewModal
